import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import {
  Box,
  Button,
  Card,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  FormControl,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { useSelector } from "react-redux";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { getRootDomain } from "../utils/domain";
import { Chip } from "@mui/material";
import LanggananDialog from "./langganan/LanggananDialog";
import PilihLokasiCabang from "./cabang/PilihLokasiCabang";
import { DialogCariUlasan, DialogIsiCabangDariMaps, usePencarianOtomatis } from "./UlasanGoogleAlat";
import PilihRadiusAbsen from "./cabang/PilihRadiusAbsen";

import { formatTanggal, labelStatus } from "./langganan/langgananUtil";
import { apiHost } from '../utils/apiHost';
import { notif, konfirmasi } from '../utils/dialog';

// Peta "Lokasi Cabang" menyesuaikan diri ke semua titik cabang (sebelumnya terpusat di koordinat
// tetap sehingga cabang di kota lain tidak terlihat).
const PasKeCabang = ({ titik }) => {
  const map = useMap();
  const kunci = titik.map((t) => t.join(",")).join("|");
  React.useEffect(() => {
    if (!titik.length) return;
    if (titik.length === 1) map.setView(titik[0], 16);
    else map.fitBounds(titik, { padding: [40, 40], maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, kunci]);
  return null;
};
const titikCabang = (daftar) =>
  (daftar || [])
    .map((c) => String(c.koordinat || "").split(",").map((v) => Number(v.trim())))
    .filter((t) => t.length === 2 && t.every(Number.isFinite));

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) => axios.get(url).then((res) => res.data);

const normalizeCabangData = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.rows)) return responseData.rows;
  return [];
};

export const Branch = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === "superadmin";
  const ownCabangUuid = user?.cabang?.uuid || user?.cabanguuid || "";
  const { data: cabangData, error: cabangError } = useSWR(`${getApiBaseUrl()}/cabang`, fetcher);
  const allCabang = normalizeCabangData(cabangData);
  const cabang = isSuperAdmin ? allCabang : allCabang.filter((c) => c.uuid === ownCabangUuid);

  const { data: paketData } = useSWR(isSuperAdmin ? `${getApiBaseUrl()}/paket` : null, (url) => axios.get(url).then((r) => r.data.data));
  const pakets = paketData || [];
  const [langgananCabang, setLanggananCabang] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const pencarianUlasan = usePencarianOtomatis();
  const [cabangBaru, setCabangBaru] = useState(null); // cabang yang baru dibuat: tawarkan tautan ulasan Google otomatis
  const [dialogMaps, setDialogMaps] = useState(false);
  const [ulasanForm, setUlasanForm] = useState(""); // tautan ulasan dari "Isi dari tautan Google Maps": disimpan bersama cabang
  const [isEditing, setIsEditing] = useState(false);
  const [currentCabang, setCurrentCabang] = useState({
    uuid: "",
    namacabang: "",
    slug: "",
    alamat: "",
    koordinat: "",
    radius: "",
    tipe_cabang: ""
  });
  const [slugError, setSlugError] = useState("");

  const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  L.Marker.prototype.options.icon = DefaultIcon;

  const handleOpenModal = (cabang = null) => {
    if (cabang) {
      console.log("Edit cabang:", cabang);
      setIsEditing(true);
      setCurrentCabang(cabang);
    } else {
      setIsEditing(false);
      setCurrentCabang({ uuid: "", namacabang: "", slug: "", alamat: "", koordinat: "", radius: "", zona_waktu: "WIB" });
    }
    setSlugError("");
    setUlasanForm("");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSlugError("");
    setUlasanForm("");
    setCurrentCabang({ uuid: "", namacabang: "", slug: "", alamat: "", koordinat: "", radius: "", tipe_cabang: "" });
  };

  // Hasil dialog "Isi dari tautan Google Maps": hanya yang dicentang pengguna; nama boleh yang diedit sendiri.
  const terapkanDariMaps = (u) => {
    setCurrentCabang((prev) => ({
      ...prev,
      ...(u.nama ? { namacabang: u.nama } : {}),
      ...(u.alamat ? { alamat: u.alamat } : {}),
      ...(u.koordinat ? { koordinat: u.koordinat } : {}),
    }));
    if (u.tautanUlasan) setUlasanForm(u.tautanUlasan);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "slug") setSlugError("");
    setCurrentCabang((prev) => ({ ...prev, [name]: value }));
  };

  // Tautan ulasan dari tautan Maps disimpan di konfigurasi cabang; gagalnya tidak membatalkan penyimpanan cabang (bisa diisi lagi di menu Ulasan Google).
  const simpanUlasanForm = async (uuid) => {
    try {
      await axios.put(`${getApiBaseUrl()}/updatekonfigurasicabang`, { cabangUuid: uuid, ulasanGoogleUrl: ulasanForm }, { withCredentials: true });
      notif.sukses("Tautan ulasan Google disimpan.");
    } catch (error) {
      notif.error(error.response?.data?.msg || error.response?.data?.message || "Cabang tersimpan, tetapi tautan ulasan gagal disimpan. Isi lagi di menu Ulasan Google.");
    }
  };

  const handleSaveCabang = async () => {
    try {
      const [latitude, longitude] = (currentCabang.koordinat || "").split(",").map((val) => val.trim());

      const payload = {
        namacabang: currentCabang.namacabang,
        alamat: currentCabang.alamat,
        latitude: latitude || null,
        longitude: longitude || null,
      };
      if (isEditing) {
        payload.slug = currentCabang.slug;
      }
      // Radius absen boleh diatur admin cabang & superadmin; tipe cabang hanya superadmin.
      payload.radius = currentCabang.radius;
      payload.zonaWaktu = currentCabang.zona_waktu || "WIB";
      if (isSuperAdmin) {
        payload.tipeCabang = currentCabang.tipe_cabang;
      }

      if (isEditing) {
        console.log("cek update:", currentCabang.uuid);
        await axios.put(`${getApiBaseUrl()}/updatecabang/${currentCabang.uuid}`, payload, {
          withCredentials: true,
        });
      } else {
        const dibuat = await axios.post(`${getApiBaseUrl()}/createcabang`, payload, {
          withCredentials: true,
        });
        const uuidBaru = dibuat.data?.data?.uuid;
        if (uuidBaru && ulasanForm) await simpanUlasanForm(uuidBaru);
        else if (uuidBaru && pencarianUlasan) setCabangBaru({ uuid: uuidBaru, nama: currentCabang.namacabang });
      }
      if (isEditing && ulasanForm) await simpanUlasanForm(currentCabang.uuid);

      mutate(`${getApiBaseUrl()}/cabang`);
      handleCloseModal();
    } catch (error) {
      if (error.response?.status === 409) {
        setSlugError(error.response.data.message);
        return;
      }
      notif.error(error.response?.data?.message || "Gagal menyimpan cabang.");
    }
  };

  const handleDeleteCabang = async (uuid) => {
    if (!await konfirmasi("Apakah Anda yakin ingin menghapus cabang ini?")) return;

    try {
      await axios.delete(`${getApiBaseUrl()}/deletecabang/${uuid}`, { withCredentials: true });
      mutate(`${getApiBaseUrl()}/cabang`);
    } catch (error) {
      notif.error(error.response?.data?.message || "Gagal menghapus cabang.");
    }
  };

  if (cabangError) return <Typography>Error loading data.</Typography>;
  if (!cabangData) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        Manajemen Cabang
      </Typography>
      {isSuperAdmin && (
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>
          Tambah Cabang
        </Button>
      )}
      <TableContainer component={Paper} sx={{ marginTop: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
            <TableCell>No</TableCell>
              <TableCell>Nama Cabang</TableCell>
              <TableCell>Subdomain</TableCell>
              <TableCell>Tipe Cabang</TableCell>
              <TableCell>Alamat</TableCell>
              <TableCell>Koordinat</TableCell>
              <TableCell>Radius Absensi</TableCell>
              <TableCell>Zona</TableCell>
              <TableCell>Paket & Masa Aktif</TableCell>
              <TableCell>Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
  {cabang.map((branch, index) => (
    <TableRow key={branch.uuid}>
      <TableCell>{index + 1}</TableCell>
      <TableCell>{branch.namacabang}</TableCell>
      <TableCell>{branch.slug || "-"}</TableCell>
      <TableCell>{branch.tipe_cabang || "-"}</TableCell>
      <TableCell>{branch.alamat}</TableCell>
      <TableCell>{branch.koordinat}</TableCell>
      <TableCell>{branch.radius ? `${Number(branch.radius).toLocaleString("id-ID")} m` : "Belum diatur"}</TableCell>
      <TableCell>{branch.zona_waktu || "WIB"}</TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {pakets.find((p) => p.uuid === branch.paketUuid)?.nama || (branch.uuid === ownCabangUuid && user?.langganan?.paketNama) || (branch.paketUuid ? "-" : "Tanpa paket")}
        </Typography>
        <Chip size="small" color={labelStatus(branch.langganan).color} label={labelStatus(branch.langganan).text} />
        {!branch.langganan?.permanen && branch.masaAktifSampai && (
          <Typography variant="caption" display="block" color="text.secondary">
            berakhir {formatTanggal(branch.masaAktifSampai)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleOpenModal(branch)}
          sx={{ marginRight: 1 }}
        >
          Edit
        </Button>
        {isSuperAdmin && (
          <Button
            variant="outlined"
            color="success"
            onClick={() => setLanggananCabang(branch)}
            sx={{ marginRight: 1 }}
          >
            Langganan
          </Button>
        )}
        {isSuperAdmin && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleDeleteCabang(branch.uuid)}
          >
            Hapus
          </Button>
        )}
      </TableCell>
    </TableRow>
  ))}
</TableBody>

        </Table>
      </TableContainer>

      {isSuperAdmin && (
        <LanggananDialog
          open={Boolean(langgananCabang)}
          cabang={langgananCabang}
          pakets={pakets}
          onClose={() => setLanggananCabang(null)}
          onSaved={() => mutate(`${getApiBaseUrl()}/cabang`)}
        />
      )}

      <DialogIsiCabangDariMaps
        open={dialogMaps}
        cabangUuid={isEditing ? currentCabang.uuid : ""}
        formSekarang={currentCabang}
        onClose={() => setDialogMaps(false)}
        onTerapkan={terapkanDariMaps}
      />

      {/* Cabang baru dibuat: cari tautan ulasan Google otomatis (bila kunci Google Places terpasang); pengguna memilih lalu tersimpan. */}
      <DialogCariUlasan
        open={Boolean(cabangBaru)}
        cabangUuid={cabangBaru?.uuid}
        namaCabang={cabangBaru?.nama}
        onClose={() => setCabangBaru(null)}
        onPilih={async (tautan) => {
          try {
            await axios.put(`${getApiBaseUrl()}/updatekonfigurasicabang`, { cabangUuid: cabangBaru.uuid, ulasanGoogleUrl: tautan }, { withCredentials: true });
            notif.sukses("Tautan ulasan Google disimpan.");
          } catch (error) {
            notif.error(error.response?.data?.msg || error.response?.data?.message || "Gagal menyimpan tautan ulasan.");
          }
        }}
      />

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
      >
        <Card sx={{ padding: 4, maxWidth: 500, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
          <Typography variant="h6" gutterBottom>
            {isEditing ? "Edit Cabang" : "Tambah Cabang"}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Button size="small" variant="outlined" onClick={() => setDialogMaps(true)}>Isi dari tautan Google Maps</Button>
            {ulasanForm && (
              <Typography data-testid="pratinjau-ulasan-cabang" variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                Tautan ulasan Google siap disimpan bersama cabang. QR-nya memakai alamat pendek milik kita yang meneruskan ke halaman ulasan.
              </Typography>
            )}
          </Box>
          <TextField
            name="namacabang"
            label="Nama Cabang"
            fullWidth
            margin="normal"
            value={currentCabang.namacabang}
            onChange={handleFormChange}
          />
          {isEditing && (
            <TextField
              name="slug"
              label="Slug / Subdomain Menu"
              fullWidth
              margin="normal"
              value={currentCabang.slug || ""}
              onChange={handleFormChange}
              error={Boolean(slugError)}
              helperText={slugError || `Menu cabang bisa diakses di ${(currentCabang.slug || "slug").toLowerCase()}.${getRootDomain()}`}
            />
          )}
          <TextField
            name="alamat"
            label="Alamat"
            fullWidth
            margin="normal"
            value={currentCabang.alamat}
            onChange={handleFormChange}
          />
          <PilihLokasiCabang
            koordinat={currentCabang.koordinat}
            radius={currentCabang.radius}
            alamat={currentCabang.alamat}
            onChange={(koordinat) => setCurrentCabang((prev) => ({ ...prev, koordinat }))}
          />
          <TextField
            name="koordinat"
            label="Koordinat (Lat,Lng)"
            fullWidth
            margin="normal"
            value={currentCabang.koordinat}
            onChange={handleFormChange}
            helperText="Terisi otomatis dari pilihan di atas; bisa juga diketik manual."
          />
          <PilihRadiusAbsen
            value={currentCabang.radius}
            onChange={(radius) => setCurrentCabang((prev) => ({ ...prev, radius }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="zona-waktu-label">Zona waktu</InputLabel>
            <Select
              labelId="zona-waktu-label"
              name="zona_waktu"
              label="Zona waktu"
              value={currentCabang.zona_waktu || "WIB"}
              onChange={handleFormChange}
            >
              <MenuItem value="WIB">WIB — UTC+7 (Jawa, Sumatra, Kalbar, Kalteng)</MenuItem>
              <MenuItem value="WITA">WITA — UTC+8 (Bali, NTB, NTT, Kalsel, Kaltim, Kaltara, Sulawesi)</MenuItem>
              <MenuItem value="WIT">WIT — UTC+9 (Maluku, Papua)</MenuItem>
            </Select>
            <FormHelperText>Jam absensi, struk, dan jam di aplikasi POS cabang ini mengikuti zona ini.</FormHelperText>
          </FormControl>
          {isSuperAdmin && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel id="tipe-cabang-label">Tipe Cabang</InputLabel>
                <Select
                  labelId="tipe-cabang-label"
                  name="tipe_cabang"
                  value={currentCabang.tipe_cabang}
                  onChange={handleFormChange}
                  label="Tipe Cabang"
                >
                  <MenuItem value="cafe">Cafe</MenuItem>
                  <MenuItem value="retail/toko">Retail/Toko</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
            <Button onClick={handleCloseModal} sx={{ marginRight: 1 }}>
              Batal
            </Button>
            <Button variant="contained" onClick={handleSaveCabang}>
              Simpan
            </Button>
          </Box>
        </Card>
      </Modal>

      <Typography variant="h5" gutterBottom sx={{ marginTop: 4 }}>
        Lokasi Cabang
      </Typography>
      <MapContainer
        center={[-7.003011, 110.411597]}
        zoom={13}
        style={{ height: "400px", width: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <PasKeCabang titik={titikCabang(cabang)} />
        {cabang.map((item) => {
          const position = item.koordinat ? item.koordinat.split(",").map(Number) : null;
          if (position) {
            return (
              <React.Fragment key={item.uuid}>
                <Marker position={position}>
                  <Popup>
                    <Typography>
                      <strong>{item.namacabang}</strong>
                      <br />
                      {item.alamat}
                      {Number(item.radius) > 0 && (<><br />Radius absen: {Number(item.radius).toLocaleString("id-ID")} m</>)}
                    </Typography>
                  </Popup>
                </Marker>
                {/* Area radius absen cabang */}
                {Number(item.radius) > 0 && (
                  <Circle center={position} radius={Number(item.radius)} pathOptions={{ color: "#1976d2", weight: 1, fillOpacity: 0.08 }} />
                )}
              </React.Fragment>
            );
          }
          return null;
        })}
      </MapContainer>
    </Box>
  );
};